import "./email-templates-page.css";
import { useContext, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import PageTitle from "../../layouts/PageTitle";
import { useTranslation } from "react-i18next";
import SubmitButton from "../../components/ui/SubmitButton";
import {
  fetchSiteSettings,
  updateSiteSettings,
} from "../../../store/custom-actions/settings-actions/siteSettingsSlicer";
import FormField from "../../components/ui/FormField";
import TextAreaField from "../../components/ui/TextAreaField";
import { emailTemplateFields } from "./emailTemplateFieldsConfig";

function EmailTemplatesPage() {
  const { t } = useTranslation();
  const { loading: submitLoading, data: settingData } = useSelector(
    (state) => state.siteSettingsSlicer
  );
  const dispatch = useDispatch();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm();


  // Flatten all fields for form initialization
  const allFields = emailTemplateFields.flatMap(category => category.fields);

  useEffect(() => {
    allFields.forEach(({ name }) => {
      const value = settingData?.[name];
      if (typeof value === "string" || typeof value === "number") {
        setValue(name, value);
      }
    });
  }, [settingData, setValue]);

  useEffect(() => {
    dispatch(fetchSiteSettings());
  }, [dispatch]);

  const onSubmit = async (data) => {
    await dispatch(updateSiteSettings(data));
  };

  const renderField = (field) => {
    const { label, name, type, required, description } = field;
    
    if (type === "richtext") {
      return (
        <div className="col-12" key={name}>
          <TextAreaField
            label={label}
            name={name}
            register={register}
            required={required}
            errors={errors}
            isRichTextEditor={true}
            watch={watch}
            setValue={setValue}
            placeholder={`Enter ${label.toLowerCase()}`}
          />
          {description && (
            <small className="text-muted d-block mt-1">
              <i className="fas fa-info-circle me-1"></i>
              {description}
            </small>
          )}
        </div>
      );
    }
    
    return (
      <div className="col-md-6" key={name}>
        <FormField
          label={label}
          name={name}
          register={register}
          required={required}
          errors={errors}
          placeholder={`Enter ${label.toLowerCase()}`}
        />
        {description && (
          <small className="text-muted d-block mt-1">
            <i className="fas fa-info-circle me-1"></i>
            {description}
          </small>
        )}
      </div>
    );
  };

  return (
    <>
      <PageTitle
        motherMenu={"Settings"}
        activeMenu={"Email Templates"}
        customClasses={"mb-0"}
      />
      
      <div className="row">
        <div className="col-xl-12 col-xxl-12">
          <form id="submit-form" onSubmit={handleSubmit(onSubmit)}>
            <div className="p-2 mb-3 mt-0">
              <SubmitButton
                isLoading={submitLoading}
                form="submit-form"
              />
            </div>

            {emailTemplateFields.map((category, categoryIndex) => (
              <div className="card mb-4" key={categoryIndex}>
                <h5 className="card-title p-4 border-bottom">
                  <i className="fas fa-envelope me-2 text-primary"></i>
                  {t(category.category)}
                </h5>
                <div className="card-body">
                  <div className="row">
                    {category.fields.map(renderField)}
                  </div>
                </div>
              </div>
            ))}

            {/* Dynamic Variables Help */}
            <div className="card">
              <h5 className="card-title p-4 border-bottom">
                <i className="fas fa-code me-2 text-info"></i>
                {t("Available Dynamic Variables")}
              </h5>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6">
                    <h6>{t("Customer Information")}</h6>
                    <ul className="list-unstyled">
                      <li><code>{`{email}`}</code> - {t("Customer email")}</li>
                      <li><code>{`{name}`}</code> - {t("Customer name")}</li>
                      <li><code>{`{phone}`}</code> - {t("Customer phone")}</li>
                    </ul>
                  </div>
                  <div className="col-md-6">
                    <h6>{t("Order Information")}</h6>
                    <ul className="list-unstyled">
                      <li><code>{`{invoice_no}`}</code> - {t("Order invoice number")}</li>
                      <li><code>{`{order_total}`}</code> - {t("Order total amount")}</li>
                      <li><code>{`{order_date}`}</code> - {t("Order date")}</li>
                    </ul>
                  </div>
                </div>
                <div className="row mt-3">
                  <div className="col-md-6">
                    <h6>{t("Site Information")}</h6>
                    <ul className="list-unstyled">
                      <li><code>{`{site_title}`}</code> - {t("Site title from settings")}</li>
                      <li><code>{`{site_url}`}</code> - {t("Site URL")}</li>
                      <li><code>{`{support_email}`}</code> - {t("Support email")}</li>
                    </ul>
                  </div>
                  <div className="col-md-6">
                    <h6>{t("Shipping Information")}</h6>
                    <ul className="list-unstyled">
                      <li><code>{`{tracking_number}`}</code> - {t("Tracking number")}</li>
                      <li><code>{`{shipping_address}`}</code> - {t("Shipping address")}</li>
                      <li><code>{`{estimated_delivery}`}</code> - {t("Estimated delivery date")}</li>
                    </ul>
                  </div>
                </div>
                <div className="alert alert-info mt-3">
                  <i className="fas fa-lightbulb me-2"></i>
                  <strong>{t("Note:")}</strong> {t("All templates support HTML formatting. Use these variables in your templates to display dynamic content.")}
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}

export default EmailTemplatesPage;
